import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import * as learningService from '@sphere/learning/data-access';
import { activityKeys } from './useActivities';
import type {
  Attempt,
  CanStartAttempt,
  StartAttemptRequestDto,
  SubmitAttemptRequestDto,
  GradeAttemptRequestDto,
  ListAttemptsRequestDto as ListAttemptsDto,
  UpdateAttemptRequestDto,
} from '@sphere/learning/domain';
import type { IOffsetPaginationResponse } from '@sphere/shared/domain';

export const attemptKeys = {
  all: ['attempts'] as const,
  lists: () => [...attemptKeys.all, 'list'] as const,
  list: (params: ListAttemptsDto) => [...attemptKeys.lists(), params] as const,
  details: () => [...attemptKeys.all, 'detail'] as const,
  detail: (id: string) => [...attemptKeys.details(), id] as const,
  result: (id: string) => [...attemptKeys.details(), id, 'result'] as const,
  byActivity: (activityId: string) =>
    [...attemptKeys.all, 'byActivity', activityId] as const,
  byUser: (userId: string) => [...attemptKeys.all, 'byUser', userId] as const,
  canStart: (activityId: string) =>
    [...attemptKeys.all, 'canStart', activityId] as const,
};

export function useAttempts(
  params: ListAttemptsDto,
  options?: Omit<
    UseQueryOptions<IOffsetPaginationResponse<Attempt>>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<IOffsetPaginationResponse<Attempt>> {
  return useQuery({
    queryKey: attemptKeys.list(params),
    queryFn: () => learningService.fetchAttempts(params),
    ...options,
  });
}

export function useAttempt(id: string): UseQueryResult<Attempt> {
  return useQuery({
    queryKey: attemptKeys.detail(id),
    queryFn: () => learningService.fetchAttemptById(id),
    enabled: !!id,
  });
}

export function useAttemptResult(
  id: string
): UseQueryResult<Attempt> {
  return useQuery({
    queryKey: attemptKeys.result(id),
    queryFn: () => learningService.fetchAttemptResult(id),
    enabled: !!id,
  });
}

export function useStartAttempt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      activityId,
      data,
    }: {
      activityId: string;
      data: StartAttemptRequestDto;
    }) => learningService.startAttempt(activityId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: attemptKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: attemptKeys.byActivity(data.activityId),
      });
      queryClient.invalidateQueries({
        queryKey: attemptKeys.byUser(data.userId),
      });
      queryClient.invalidateQueries({
        queryKey: attemptKeys.canStart(variables.activityId),
      });
    },
  });
}

export function useCanStartAttempt({
  activityId,
}: {
  activityId: string;
}): UseQueryResult<CanStartAttempt> {
  return useQuery({
    queryKey: attemptKeys.canStart(activityId),
    queryFn: () => learningService.canStartAttempt(activityId),
    enabled: !!activityId,
  });
}

export function useUpdateAttempt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateAttemptRequestDto) => {
      const { id, ...updateData } = data;
      return learningService.updateAttempt(id, {
        ...updateData,
        id, // Le service attend le DTO complet avec l'id
      } as UpdateAttemptRequestDto);
    },
    onSuccess: (data: Attempt) => {
      queryClient.invalidateQueries({ queryKey: attemptKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: attemptKeys.lists() });
    },
  });
}

export function useSubmitAttempt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data?: SubmitAttemptRequestDto;
    }) => learningService.submitAttempt(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: attemptKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: attemptKeys.lists() });
      queryClient.invalidateQueries({ queryKey: activityKeys.remediation() });
    },
  });
}

export function useDeleteAttempt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => learningService.deleteAttempt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attemptKeys.lists() });
    },
  });
}

export function useGradeAttempt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: GradeAttemptRequestDto;
    }) => learningService.gradeAttempt(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: attemptKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: attemptKeys.lists() });
    },
  });
}
